'use client'

import type { CSSProperties, ReactElement, ReactNode } from 'react'

/**
 * One segment. `value` is the rule value the segment stands for and its React
 * key; `icon` is decorative and optional — the word is what names the segment,
 * on screen and to a reader.
 */
export type BirdieSegItem<T extends string = string> = Readonly<{
  value: T
  label: string
  icon?: ReactNode
}>

export type BirdieAutoBetSegProps<T extends string> = Readonly<{
  /** The group's accessible name — the field's own word. */
  label: string
  items: readonly BirdieSegItem<T>[]
  /**
   * The checked value, or `null` when the player has set something none of
   * these segments describes (a zone dragged by hand). Nothing is checked then
   * and the travelling thumb retracts, which is the control saying so.
   */
  value: T | null
  onPick: (value: T) => void
}>

/**
 * The sheet's SWITCH SET (owner 2026-09-18: "maybe the short, middle, long and
 * far could be proper like switches maybe"). One recessed track, N equal
 * segments, and a single gold thumb that TRAVELS to the checked one — so the
 * four zones and the three markets read as one physical control with a knob in
 * it rather than as a row of loose pills, and both rows are the same object at
 * two lengths.
 *
 * The thumb is a `<span>`, `aria-hidden`, positioned entirely from two custom
 * properties the group carries (`--seg-n`, `--seg-i`): its width is a segment's
 * width and it slides by whole multiples of itself, so the drawing cannot
 * drift from the buttons however long the words are.
 *
 * Accessibility is the shipped contract, unchanged: `role="radiogroup"` named
 * by the field, `role="radio"` + `aria-checked` per segment. The thumb is
 * decoration over the top of that and is never the state a reader is told.
 *
 * Generic in the rule's own union, so the zone row hands back a `ZonePreset`
 * and the markets row a `ZoneMarkets` with no cast at either call site.
 */
export function BirdieAutoBetSeg<T extends string>({
  label,
  items,
  value,
  onPick
}: BirdieAutoBetSegProps<T>): ReactElement {
  // `indexOf` over the mapped values rather than `findIndex`: same answer, and
  // it is the ES5 pair of methods the rest of this board is written in.
  const index = value === null ? -1 : items.map((item) => item.value).indexOf(value)
  const checked = index >= 0

  return (
    <div
      aria-label={label}
      className={`birdie-autobet-seg${checked ? '' : ' birdie-autobet-seg--none'}`}
      role="radiogroup"
      style={
        {
          '--seg-n': String(items.length),
          '--seg-i': String(checked ? index : 0)
        } as CSSProperties
      }
    >
      <span aria-hidden="true" className="birdie-autobet-seg__thumb" />
      {items.map((item) => (
        <button
          aria-checked={item.value === value}
          className={`birdie-autobet-seg__btn${item.value === value ? ' birdie-autobet-seg__btn--on' : ''}`}
          key={item.value}
          onClick={() => {
            onPick(item.value)
          }}
          role="radio"
          type="button"
        >
          {item.icon === undefined ? null : (
            <span aria-hidden="true" className="birdie-autobet-seg__ico">
              {item.icon}
            </span>
          )}
          <span className="birdie-autobet-seg__word">{item.label}</span>
        </button>
      ))}
    </div>
  )
}
